const colors = {
  alkalinity: '#3453db',
  temperature: '#e74c3c',
  conductivity: '#2ecc71',
  ph: '#9b59b6'
}

/** Aggregation interval options (value in milliseconds) */
export const AGGREGATION_INTERVALS = {
  raw: null,
  '10min': 10 * 60 * 1000,
  '60min': 60 * 60 * 1000,
  '1day': 24 * 60 * 60 * 1000,
  '1week': 7 * 24 * 60 * 60 * 1000
}

const NUMERIC_FIELDS = [
  'total_alkalinity_ppm_rolling_avg',
  'ph',
  'temperature_c',
  'conductivity_mscm'
]

/**
 * Aggregate measurement data into time buckets (e.g. 10 min avg, 60 min avg).
 * @param {Array} data - Raw measurement objects with timestamp and numeric fields
 * @param {number|null} intervalMs - Bucket size in ms, or null for no aggregation
 * @returns {Array} Aggregated data
 */
export function aggregateData(data, intervalMs) {
  if (!data || data.length === 0) return data
  if (!intervalMs) return data

  const buckets = new Map()

  for (const row of data) {
    const ts = new Date(row.timestamp).getTime()
    const bucketKey = Math.floor(ts / intervalMs) * intervalMs

    if (!buckets.has(bucketKey)) {
      buckets.set(bucketKey, { timestamp: new Date(bucketKey).toISOString(), counts: {}, sums: {} })
    }
    const b = buckets.get(bucketKey)

    for (const field of NUMERIC_FIELDS) {
      const v = row[field]
      if (v !== undefined && v !== null && !Number.isNaN(v)) {
        b.sums[field] = (b.sums[field] ?? 0) + v
        b.counts[field] = (b.counts[field] ?? 0) + 1
      }
    }
  }

  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, b]) => {
      const out = { timestamp: b.timestamp }
      for (const field of NUMERIC_FIELDS) {
        const n = b.counts[field]
        out[field] = n ? b.sums[field] / n : null
      }
      return out
    })
}

const chartDefaults = {
  alkalinity: 140,
  temperature: 40,
  ph: 12,
  conductivity: 10
}

export function drawSparkline(svg, name, data, decimals, isHistorical = false) {
  if (!data || data.length < 2) return null

  const rect = svg.getBoundingClientRect()
  const width = rect.width || 400
  const height = rect.height || 80
  svg.innerHTML = ''

  const values = data.map(d => d.value)
  const dataMin = Math.min(...values)
  const dataMax = Math.max(...values)
  
  const axisMin = 0
  const defaultMax = chartDefaults[name] || 100
  const axisMax = Math.max(dataMax, defaultMax)
  const axisRange = axisMax - axisMin || 1

  const timestamps = data.map(d => new Date(d.timestamp).getTime())
  const minTime = Math.min(...timestamps)
  const maxTime = Math.max(...timestamps)
  const timeRange = maxTime - minTime || 1

  const padTop = 20
  const padBottom = 25
  const padSide = 10
  const innerHeight = Math.max(0, height - padTop - padBottom)
  const innerWidth = Math.max(0, width - padSide * 2)

  const points = data.map(d => {
    const pointTime = new Date(d.timestamp).getTime()
    const normalized = (d.value - axisMin) / axisRange
    const x = padSide + ((pointTime - minTime) / timeRange) * innerWidth
    const y = padTop + (1 - normalized) * innerHeight
    return { x, y, value: d.value, ts: d.timestamp }
  })

  const pointsSorted = points.slice().sort((a, b) => a.x - b.x)

  // Area
  const area = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  const topPath = pointsSorted.map(p => `${p.x},${p.y}`).join(' L ')
  const bottomY = height - padBottom
  const areaD = `M ${pointsSorted[0].x},${bottomY} L ${topPath} L ${pointsSorted[pointsSorted.length - 1].x},${bottomY} Z`
  area.setAttribute('d', areaD)
  area.setAttribute('fill', colors[name] + '20')

  // Line
  const line = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  line.setAttribute('d', `M ${topPath}`)
  line.setAttribute('fill', 'none')
  line.setAttribute('stroke', colors[name])
  line.setAttribute('stroke-width', '3')
  line.setAttribute('stroke-linecap', 'round')

  svg.appendChild(area)
  svg.appendChild(line)

  // Points with tooltips
  const tooltip = document.getElementById('chart-tooltip')
  pointsSorted.forEach(p => {
    const visibleCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    visibleCircle.setAttribute('cx', p.x)
    visibleCircle.setAttribute('cy', p.y)
    visibleCircle.setAttribute('r', '4')
    visibleCircle.setAttribute('fill', colors[name])
    visibleCircle.setAttribute('stroke', 'white')
    visibleCircle.setAttribute('stroke-width', '2')
    svg.appendChild(visibleCircle)

    const hoverCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    hoverCircle.setAttribute('cx', p.x)
    hoverCircle.setAttribute('cy', p.y)
    hoverCircle.setAttribute('r', 15)
    hoverCircle.setAttribute('fill', 'transparent')
    hoverCircle.setAttribute('stroke', 'none')
    hoverCircle.style.cursor = 'pointer'

    hoverCircle.addEventListener('mouseenter', (ev) => {
      visibleCircle.setAttribute('r', '6')
      visibleCircle.setAttribute('stroke-width', '3')
      
      const valueText = Number(p.value).toFixed(decimals)
      const timeText = isHistorical 
        ? new Date(p.ts).toLocaleString()
        : new Date(p.ts).toLocaleTimeString()
      tooltip.innerHTML = `<strong>${valueText}</strong> <span style="color:#ddd; font-size:0.85em">${timeText}</span>`
      tooltip.style.display = 'block'
      tooltip.style.fontWeight = '600'
      tooltip.style.position = 'fixed'
      tooltip.style.zIndex = '9999'
      tooltip.style.pointerEvents = 'none'
      
      const rect = tooltip.getBoundingClientRect()
      let left = ev.clientX + 12
      if (left + rect.width + 8 > window.innerWidth) {
        left = ev.clientX - rect.width - 12
      }
      let top = ev.clientY - rect.height - 12
      if (top < 4) top = ev.clientY + 12
      tooltip.style.left = left + 'px'
      tooltip.style.top = top + 'px'
    })
    
    hoverCircle.addEventListener('mousemove', (ev) => {
      const rect = tooltip.getBoundingClientRect()
      let left = ev.clientX + 12
      if (left + rect.width + 8 > window.innerWidth) {
        left = ev.clientX - rect.width - 12
      }
      let top = ev.clientY - rect.height - 12
      if (top < 4) top = ev.clientY + 12
      tooltip.style.left = left + 'px'
      tooltip.style.top = top + 'px'
    })
    
    hoverCircle.addEventListener('mouseleave', () => {
      visibleCircle.setAttribute('r', '4')
      visibleCircle.setAttribute('stroke-width', '2')
      tooltip.style.display = 'none'
    })

    svg.appendChild(hoverCircle)
  })

  // Time labels
  function createLabel(text, x, y, anchor = 'start') {
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    bg.setAttribute('fill', 'rgba(255, 255, 255, 0.9)')
    bg.setAttribute('rx', '3')
    
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    label.setAttribute('x', x)
    label.setAttribute('y', y)
    label.setAttribute('fill', '#666')
    label.setAttribute('font-size', '10')
    label.setAttribute('font-family', 'Segoe UI, system-ui, sans-serif')
    label.setAttribute('text-anchor', anchor)
    label.textContent = text
    
    svg.appendChild(label)
    
    const bbox = label.getBBox()
    bg.setAttribute('x', bbox.x - 2)
    bg.setAttribute('y', bbox.y - 1)
    bg.setAttribute('width', bbox.width + (isHistorical ? 4 : 8))
    bg.setAttribute('height', bbox.height + (isHistorical ? 2 : 8))
    
    svg.insertBefore(bg, label)
  }

  const startTime = new Date(minTime)
  const endTime = new Date(maxTime)
  
  if (isHistorical) {
    createLabel(
      startTime.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }), 
      2, height - 5, 'start'
    )
    createLabel(
      endTime.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }), 
      width - 2, height - 5, 'end'
    )
  } else {
    const durationMins = Math.round(timeRange / 60000)
    createLabel(startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), 2, height - 5, 'start')
    createLabel(`${durationMins} min`, width / 2, height - 5, 'middle')
    createLabel(endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), width - 5, height - 5, 'end')
  }

  // Return stats
  const avg = values.reduce((a, b) => a + b, 0) / values.length
  return { min: dataMin, avg, max: dataMax }
}
